import React, { useState } from "react";
import PropTypes from "prop-types";

// MUI stuff
import { Button, Typography } from "@mui/material";
import Alert from "@mui/material/Alert";
import { makeStyles } from "tss-react/mui";
import PublishIcon from "@mui/icons-material/Publish";

// Redux stuff
import { connect } from "react-redux";
import {
  setShopListings,
  deactivateAllShopProfiles,
} from "../../redux/actions/dataActions";

// CSV parsing
import Papa from "papaparse";
import { createRandomId } from "../TemplateBuilder/Elements/StandardElementProperties";

const DEMO_CSV = `title,description,price,url,categories,images
Handmade ceramic mug,A hand-thrown mug with a speckled glaze. Dishwasher safe.,24.00,https://example.com/products/ceramic-mug,"Home,Kitchen",https://picsum.photos/id/225/800/800
Linen tote bag,Reusable tote bag made from organic linen. Fits a laptop and groceries.,18.50,https://example.com/products/linen-tote,"Bags,Accessories",https://picsum.photos/id/1011/800/800
Soy candle set,Set of three soy candles in seasonal scents.,32.00,https://example.com/products/soy-candle-set,"Home,Gifts","https://picsum.photos/id/1060/800/800,https://picsum.photos/id/1080/800/800"
`;

const useStyles = makeStyles()((theme) => ({
  button: {},
  errorAlertDiv: {
    paddingTop: theme.spacing(1),
  },
  uploadCSVDiv: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 5,
  },
  demoFileText: {
    fontSize: 15,
  },
  buttonDiv: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    minWidth: 220,
  },
  publishIcon: {
    height: 30,
    padding: theme.spacing(1),
  },
}));

function getField(product, keys) {
  for (const key of keys) {
    const match = Object.keys(product).find(
      (productKey) => productKey.trim().toLowerCase() === key
    );
    if (match && product[match] != null && String(product[match]).trim() !== "") {
      return String(product[match]).trim();
    }
  }
  return "";
}

function splitList(value) {
  if (!value) return [];
  return String(value)
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function getImageUrls(product) {
  const combinedImages = splitList(
    getField(product, ["images", "image_urls", "imageurls"])
  );
  const numberedImages = Object.keys(product)
    .filter((key) => /^image\d*$/i.test(key.trim()))
    .sort()
    .map((key) => String(product[key] || "").trim())
    .filter(Boolean);

  return [...new Set([...combinedImages, ...numberedImages])];
}

export function formatCsvProducts(products) {
  return products
    .map((product, index) => {
      const title = getField(product, [
        "title",
        "name",
        "product",
        "product_name",
        "productname",
      ]);
      const description = getField(product, [
        "description",
        "short_description",
        "shortdescription",
        "body",
      ]);
      const url = getField(product, ["url", "link", "permalink", "product_url"]);
      const images = getImageUrls(product).map((imageURL) => ({
        full: imageURL,
        medium: imageURL,
        small: imageURL,
      }));

      return {
        id: getField(product, ["id", "sku"]) || `csv-${index}-${createRandomId()}`,
        title,
        description,
        price: getField(product, ["price", "sale_price", "regular_price"]),
        url,
        categories: splitList(
          getField(product, ["categories", "category", "tags", "tag"])
        ),
        images,
        isChecked: false,
        numberOfPostsToGenerate: 5,
        source: "csv",
      };
    })
    .filter((product) => product.title || product.url || product.images.length > 0);
}

function UploadProductsViaCSV(props) {
  const { classes } = useStyles();

  const [errorText, setErrorText] = useState("");

  const handleDownloadDemo = (event) => {
    event.preventDefault();
    const blob = new Blob([DEMO_CSV], { type: "text/csv;charset=utf-8;" });
    const downloadUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = "products-demo.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(downloadUrl);
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];

    if (!file) {
      setErrorText("Please select a CSV file to upload.");
      return;
    }

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (header) => header.trim(),
      complete: (results) => {
        const formattedProducts = formatCsvProducts(results.data || []);

        if (formattedProducts.length === 0) {
          setErrorText(
            "No products found. Include a title, url, or images column."
          );
          event.target.value = "";
          return;
        }

        props.setShopListings(formattedProducts);
        props.deactivateAllShopProfiles().catch(() => {
          // CSV listings still load if the user has no shops to deactivate
        });
        setErrorText("");
        event.target.value = "";
      },
      error: () => {
        setErrorText("Failed to parse CSV file.");
        event.target.value = "";
      },
    });
  };

  return (
    <div>
      <div className={classes.uploadCSVDiv}>
        <input
          accept=".csv"
          id="csv-file"
          type="file"
          onChange={handleFileChange}
          style={{ display: "none" }}
        />
        <label htmlFor="csv-file">
          <Button variant="outlined" color="primary" component="span">
            <div className={classes.buttonDiv}>
              <PublishIcon className={classes.publishIcon} />
              Upload Products via CSV
            </div>
          </Button>
        </label>

        <Typography variant="body2" className={classes.demoFileText}>
          <a href="#demo-csv" onClick={handleDownloadDemo}>
            (Demo file)
          </a>
        </Typography>
      </div>

      {errorText.length > 0 && (
        <div className={classes.errorAlertDiv}>
          <Alert severity="error" onClose={() => setErrorText("")}>
            {errorText}
          </Alert>
        </div>
      )}
    </div>
  );
}

UploadProductsViaCSV.propTypes = {
  setShopListings: PropTypes.func.isRequired,
  deactivateAllShopProfiles: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({});

export default connect(mapStateToProps, {
  setShopListings,
  deactivateAllShopProfiles,
})(UploadProductsViaCSV);
